/**
 * WAYFX ANNOUNCEMENT BAR - RHEAL STYLE
 * Enhanced announcement bar with Splide.js integration
 * Matches rhealsuperfoods.com functionality exactly
 */

class WayfxAnnouncementBar {
  constructor(element) {
    this.element = element;
    this.splideInstance = null;
    this.config = this.parseConfig();
    this.isVisible = true;
    this.isInitialized = false;

    this.init();
  }

  /**
   * Parse configuration from data attributes
   */
  parseConfig() {
    try {
      const configData = this.element.dataset.splide;
      return configData ? JSON.parse(configData) : this.getDefaultConfig();
    } catch (error) {
      console.warn(
        "Failed to parse announcement bar config, using defaults:",
        error
      );
      return this.getDefaultConfig();
    }
  }

  /**
   * Default Splide configuration matching Rheal's setup
   */
  getDefaultConfig() {
    return {
      type: "loop",
      autoplay: true,
      interval: 4000,
      pauseOnHover: true,
      resetProgress: false,
      arrows: true,
      pagination: false,
      drag: true,
      keyboard: true,
      focus: "center",
      speed: 800,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      gap: 0,
      perPage: 1,
      perMove: 1,
      trimSpace: false,
      breakpoints: {
        768: {
          arrows: false,
          drag: true,
          autoplay: true,
        },
      },
    };
  }

  /**
   * Initialize the announcement bar
   */
  init() {
    if (!this.element || this.isInitialized) return;

    // Check if Splide is available
    if (typeof Splide === "undefined") {
      console.warn("Splide.js is not loaded. Loading from CDN...");
      this.loadSplideLibrary().then(() => {
        this.initializeSplide();
      });
      return;
    }

    this.initializeSplide();
  }

  /**
   * Load Splide.js library dynamically if not available
   */
  loadSplideLibrary() {
    return new Promise((resolve, reject) => {
      // Load CSS
      const cssLink = document.createElement("link");
      cssLink.rel = "stylesheet";
      cssLink.href =
        "https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css";
      document.head.appendChild(cssLink);

      // Load JS
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Splide slider
   */
  initializeSplide() {
    try {
      // Merge default config with element config
      const finalConfig = { ...this.getDefaultConfig(), ...this.config };

      // Initialize Splide
      this.splideInstance = new Splide(this.element, finalConfig);

      // Add event listeners
      this.addEventListeners();

      // Mount the slider
      this.splideInstance.mount();

      // Mark as initialized
      this.isInitialized = true;
      this.element.classList.add("is-initialized");

      // Auto-hide arrows if only one slide
      this.handleArrowVisibility();

      // Handle accessibility
      this.enhanceAccessibility();

      console.log("Wayfx Announcement Bar initialized successfully");
    } catch (error) {
      console.error("Failed to initialize announcement bar:", error);
      // Fallback: show first slide statically
      this.fallbackMode();
    }
  }

  /**
   * Add event listeners for enhanced functionality
   */
  addEventListeners() {
    if (!this.splideInstance) return;

    // Pause on hover (if enabled)
    if (this.config.pauseOnHover) {
      this.element.addEventListener("mouseenter", () => {
        this.splideInstance.Components.Autoplay.pause();
      });

      this.element.addEventListener("mouseleave", () => {
        this.splideInstance.Components.Autoplay.play();
      });
    }

    // Handle visibility change (pause when tab is inactive)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.splideInstance.Components.Autoplay.pause();
      } else if (this.isVisible) {
        this.splideInstance.Components.Autoplay.play();
      }
    });

    // Keyboard navigation
    this.element.addEventListener("keydown", this.handleKeydown.bind(this));

    // Touch/swipe enhancements for mobile
    if (this.isMobile()) {
      this.addMobileEnhancements();
    }

    // Custom events
    this.splideInstance.on("mounted", () => {
      this.element.setAttribute("aria-live", "polite");
    });

    this.splideInstance.on("moved", (newIndex) => {
      this.updateAriaLabels(newIndex);
    });
  }

  /**
   * Handle keyboard navigation
   */
  handleKeydown(event) {
    if (!this.splideInstance) return;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        this.splideInstance.go("<");
        break;
      case "ArrowRight":
        event.preventDefault();
        this.splideInstance.go(">");
        break;
      case "Home":
        event.preventDefault();
        this.splideInstance.go(0);
        break;
      case "End":
        event.preventDefault();
        this.splideInstance.go(this.splideInstance.length - 1);
        break;
      case " ":
      case "Escape":
        event.preventDefault();
        this.splideInstance.Components.Autoplay.pause();
        break;
    }
  }

  /**
   * Add mobile-specific enhancements
   */
  addMobileEnhancements() {
    // Add swipe indicators
    this.element.style.cursor = "grab";

    // Prevent text selection during swipe
    this.element.addEventListener("selectstart", (e) => {
      if (this.splideInstance.state.is(MOVING)) {
        e.preventDefault();
      }
    });
  }

  /**
   * Handle arrow visibility based on slide count
   */
  handleArrowVisibility() {
    const slides = this.element.querySelectorAll(".splide__slide");
    const arrows = this.element.querySelector(
      ".wayfx-announcement-bar__arrows"
    );

    if (!arrows) return;

    if (slides.length <= 1) {
      arrows.style.display = "none";
    } else {
      // Show arrows on hover for desktop
      if (!this.isMobile()) {
        this.element.addEventListener("mouseenter", () => {
          arrows.style.opacity = "1";
        });

        this.element.addEventListener("mouseleave", () => {
          arrows.style.opacity = "0.7";
        });
      }
    }
  }

  /**
   * Enhance accessibility
   */
  enhanceAccessibility() {
    // Update slide labels
    const slides = this.element.querySelectorAll(".splide__slide");
    slides.forEach((slide, index) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "slide");
      slide.setAttribute("aria-label", `${index + 1} of ${slides.length}`);
    });

    // Update track
    const track = this.element.querySelector(".splide__track");
    if (track) {
      track.setAttribute("aria-live", "polite");
      track.setAttribute("aria-atomic", "false");
    }

    // Update arrows
    const prevArrow = this.element.querySelector(".splide__arrow--prev");
    const nextArrow = this.element.querySelector(".splide__arrow--next");

    if (prevArrow) {
      prevArrow.setAttribute("aria-label", "Go to previous announcement");
    }
    if (nextArrow) {
      nextArrow.setAttribute("aria-label", "Go to next announcement");
    }
  }

  /**
   * Update ARIA labels when slide changes
   */
  updateAriaLabels(activeIndex) {
    const slides = this.element.querySelectorAll(".splide__slide");
    slides.forEach((slide, index) => {
      if (index === activeIndex) {
        slide.setAttribute("aria-hidden", "false");
        slide.setAttribute("tabindex", "0");
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.setAttribute("tabindex", "-1");
      }
    });
  }

  /**
   * Fallback mode if Splide fails to initialize
   */
  fallbackMode() {
    const slides = this.element.querySelectorAll(".splide__slide");
    if (slides.length > 0) {
      // Hide all slides except the first
      slides.forEach((slide, index) => {
        slide.style.display = index === 0 ? "flex" : "none";
      });

      // Hide arrows
      const arrows = this.element.querySelector(
        ".wayfx-announcement-bar__arrows"
      );
      if (arrows) {
        arrows.style.display = "none";
      }
    }
  }

  /**
   * Check if device is mobile
   */
  isMobile() {
    return window.innerWidth < 768;
  }

  /**
   * Show the announcement bar
   */
  show() {
    this.isVisible = true;
    this.element.style.display = "block";
    if (this.splideInstance && this.splideInstance.Components.Autoplay) {
      this.splideInstance.Components.Autoplay.play();
    }
  }

  /**
   * Hide the announcement bar
   */
  hide() {
    this.isVisible = false;
    this.element.style.display = "none";
    if (this.splideInstance && this.splideInstance.Components.Autoplay) {
      this.splideInstance.Components.Autoplay.pause();
    }
  }

  /**
   * Destroy the slider
   */
  destroy() {
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.splideInstance = null;
    }
    this.isInitialized = false;
    this.element.classList.remove("is-initialized");
  }

  /**
   * Reinitialize the slider (useful for dynamic content)
   */
  refresh() {
    this.destroy();
    this.init();
  }
}

/**
 * Copy discount code functionality with enhanced UX
 */
function copyAnnouncementCode(event) {
  event.preventDefault();
  event.stopPropagation();

  const button = event.currentTarget;
  const code = button.dataset.code?.trim();

  if (!code) {
    console.warn("No discount code found");
    return;
  }

  // Copy to clipboard
  copyToClipboard(code)
    .then(() => {
      showCopySuccess(button);

      // Track event if analytics is available
      if (typeof gtag !== "undefined") {
        gtag("event", "discount_code_copied", {
          event_category: "engagement",
          event_label: code,
        });
      }
    })
    .catch((error) => {
      console.error("Failed to copy code:", error);
      showCopyError(button);
    });
}

/**
 * Enhanced clipboard copy with multiple fallbacks
 */
async function copyToClipboard(text) {
  // Modern clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      console.warn("Clipboard API failed, trying fallback:", error);
    }
  }

  // Fallback method
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.cssText =
    "position:fixed;left:-999999px;top:-999999px;opacity:0;";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    if (!successful) {
      throw new Error("execCommand returned false");
    }
  } finally {
    document.body.removeChild(textArea);
  }
}

/**
 * Show copy success feedback
 */
function showCopySuccess(button) {
  const originalContent = button.innerHTML;
  const originalOpacity = button.style.opacity;

  button.innerHTML = "✓ Copied!";
  button.style.opacity = "0.8";
  button.setAttribute("aria-label", "Discount code copied successfully");

  setTimeout(() => {
    button.innerHTML = originalContent;
    button.style.opacity = originalOpacity;
    button.removeAttribute("aria-label");
  }, 2000);
}

/**
 * Show copy error feedback
 */
function showCopyError(button) {
  const originalContent = button.innerHTML;

  button.innerHTML = "❌ Failed to copy";
  button.style.opacity = "0.7";

  setTimeout(() => {
    button.innerHTML = originalContent;
    button.style.opacity = "1";
  }, 2000);
}

/**
 * Legacy support for existing copyCode function
 */
function copyCode(event) {
  copyAnnouncementCode(event);
}

/**
 * Initialize all announcement bars when DOM is ready
 */
function initializeAnnouncementBars() {
  const announcementBars = document.querySelectorAll(".wayfx-announcement-bar");
  const instances = [];

  announcementBars.forEach((element) => {
    try {
      const instance = new WayfxAnnouncementBar(element);
      instances.push(instance);
    } catch (error) {
      console.error("Failed to initialize announcement bar:", element, error);
    }
  });

  // Store instances globally for potential external access
  window.wayfxAnnouncementBars = instances;

  return instances;
}

/**
 * Auto-initialization
 */
document.addEventListener("DOMContentLoaded", initializeAnnouncementBars);

// Also initialize if DOM is already loaded (for dynamic content)
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeAnnouncementBars);
} else {
  initializeAnnouncementBars();
}

/**
 * Handle theme editor updates (Shopify specific)
 */
document.addEventListener("shopify:section:load", function (event) {
  const announcementBar = event.target.querySelector(".wayfx-announcement-bar");
  if (announcementBar) {
    new WayfxAnnouncementBar(announcementBar);
  }
});

document.addEventListener("shopify:section:unload", function (event) {
  const announcementBar = event.target.querySelector(".wayfx-announcement-bar");
  if (announcementBar && window.wayfxAnnouncementBars) {
    const instance = window.wayfxAnnouncementBars.find(
      (inst) => inst.element === announcementBar
    );
    if (instance) {
      instance.destroy();
      window.wayfxAnnouncementBars = window.wayfxAnnouncementBars.filter(
        (inst) => inst !== instance
      );
    }
  }
});

/**
 * Export for external use
 */
window.WayfxAnnouncementBar = WayfxAnnouncementBar;

